/**
 * LakshmiDwadashamshaSection - Registration for D12 Lakshmi Dwadashamsha table
 * Demonstrates advanced migration with Surya-referenced calculations
 */

import { enhancedRegistry } from '../registry/EnhancedSectionRegistry';
import { LakshmiDwadashamshaInfoTable } from '../components/LakshmiDwadashamshaInfoTable';
import { JatakaResponse } from '../../../../../models/api';

// Import calculator to ensure it's registered
import '../calculators/LakshmiDwadashamshaCalculator';

/**
 * Custom loading component for D12 Lakshmi
 */
const LakshmiLoadingSpinner: React.FC<{ progress?: number }> = ({ progress = 0 }) => {
    return (
        <div style={{
            padding: '20px',
            textAlign: 'center',
            background: '#f8d7da',
            borderRadius: '8px',
            border: '1px solid #f1aeb5'
        }}>
            <div style={{
                fontSize: '14px',
                color: '#721c24',
                marginBottom: '12px'
            }}>
                🌺 Calculating D12 Lakshmi Dwadashamsha...
            </div>
            {progress > 0 && (
                <div style={{
                    width: '100%',
                    height: '4px',
                    backgroundColor: '#f1aeb5',
                    borderRadius: '2px',
                    overflow: 'hidden',
                    marginBottom: '8px'
                }}>
                    <div style={{
                        width: `${progress}%`,
                        height: '100%',
                        backgroundColor: '#dc3545',
                        transition: 'width 0.3s ease'
                    }} />
                </div>
            )}
            <div style={{
                fontSize: '12px',
                color: '#6c757d'
            }}>
                {progress > 0 ? `${Math.round(progress)}% complete` : 'Referencing Surya position...'}
            </div>
        </div>
    );
};

// Register the enhanced section
enhancedRegistry.register({
    id: 'lakshmi-dwadashamsha-d12',
    parentSection: 'Divisional',
    label: 'D12 Lakshmi Dwadashamsha',
    component: LakshmiDwadashamshaInfoTable,
    type: 'table',
    order: 30, // After D7 and D8

    // Lazy loading with D12 calculation
    lazy: {
        calculator: 'lakshmi-dwadashamsha-d12',
        loader: LakshmiLoadingSpinner,
        dependencies: [
            'graha_positions.planets',
            'detailed_panchanga.hora_details'
        ]
    },

    // State persistence for complex table
    persistence: {
        enabled: true,
        stateKeys: ['expanded', 'sortOrder', 'filters', 'suryaReference'],
        namespace: 'lakshmi-dwadashamsha-d12'
    },

    // Event monitoring disabled for production
    events: {},

    // Show only if we have planet data and Surya
    condition: (apiResponse: JatakaResponse) => {
        const hasPlants = apiResponse.graha_positions?.planets && apiResponse.graha_positions.planets.length > 0;
        const hasSurya = apiResponse.graha_positions?.planets?.some(p => p.short_name === 'Sy');
        return hasPlants && hasSurya;
    },

    // Component props for D12 features
    componentProps: {
        showSuryaReference: true,
        enableLakshmiDescriptions: true,
        showCalculationDetails: true,
        highlightSuryaInvariant: true
    }
});

// Export for backward compatibility
export { LakshmiDwadashamshaInfoTable };
export default LakshmiDwadashamshaInfoTable;